/* eslint-disable jsx-a11y/no-static-element-interactions */
import Slider from '@/components/Slider';
import useIsMobile from '@/hooks/useIsMobile';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useHomeStore } from '../useHome';
import { useEffect, useState } from 'react';
import {
  Enum_Completedtask_Type,
  FetchUserStreakQuery,
  FetchUserStreakQueryVariables,
  QuoteEntity,
} from '@/graphql/generated/graphql';
import './style.css';
import { useQuery } from '@apollo/client';
import { QUERY_FETCH_USER_STREAK } from '@/graphql/queries/daily/completed-tasks';
import { parseTextWithLinks } from '@/utils/navigation';

export const gradients = {
  insight: 'insight-class',
  mindful: 'mindful-class',
  emotion: 'emotion-class',
  quote: 'quote-class',
};

export default function DailyBubble() {
  const [isLoading, setIsLoading] = useState(true);
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const {
    dailyQuoteCompleted,
    dailyInsightCompleted,
    dailyEmotionWheelCompleted,
    mindfulMinuteCompleted,
    page,
    initDailyData,
    quote,
    dailyInsight,
    mindfulMinute,
    handleCompleteTask,
  } = useHomeStore();

  const { data: emotionWheelData } = useQuery<FetchUserStreakQuery, FetchUserStreakQueryVariables>(
    QUERY_FETCH_USER_STREAK,
    {
      variables: {
        type: 'EMOTIONAL_WHEEL',
      },
    },
  );

  const { data: insightData } = useQuery<FetchUserStreakQuery, FetchUserStreakQueryVariables>(QUERY_FETCH_USER_STREAK, {
    variables: {
      type: 'INSIGHT',
    },
  });

  const { data: mindfulMinuteData } = useQuery<FetchUserStreakQuery, FetchUserStreakQueryVariables>(
    QUERY_FETCH_USER_STREAK,
    {
      variables: { type: 'MINDFUL_MINUTE' },
    },
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await initDailyData();
      setIsLoading(false);
    })();
  }, [initDailyData]);

  useEffect(() => {
    if (dailyQuoteCompleted) {
      return;
    }
    async function completeQuote() {
      await handleCompleteTask({
        entity: quote,
        type: Enum_Completedtask_Type.Quote,
      });
    }
    completeQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyQuoteCompleted]);

  const hasContent = {
    insight: !!dailyInsight,
    mindful: !!mindfulMinute,
    emotion: true,
    quote: !!quote,
  };

  function addQuotes(text: string): string {
    const startsWithSmartQuote = text.startsWith('“') || text.startsWith('"');
    const endsWithSmartQuote = text.endsWith('”') || text.endsWith('"');

    if (startsWithSmartQuote && endsWithSmartQuote) {
      return text;
    } else {
      return `“${text}”`;
    }
  }

  const renderTaskCard = (type: 'insight' | 'mindful' | 'emotion' | 'quote') => {
    const title = {
      insight: dailyInsightCompleted
        ? page?.finishingCard?.finishingCardInsight?.title
        : page?.dailySection.insightTitle,
      mindful: mindfulMinuteCompleted
        ? page?.finishingCard?.finishingCardMindfulMinute?.title
        : page?.dailySection.mindfulMinuteTitle,
      emotion: dailyEmotionWheelCompleted
        ? page?.finishingCard?.finishingCardEmotionWheel?.title
        : page?.dailySection.emotionWheelTItle,
      quote: page?.dailySection.dailyQuoteTitle,
    };

    const description = {
      insight: dailyInsightCompleted ? (
        <>
          {page?.finishingCard?.finishingCardInsight?.youHaveRead}{' '}
          <strong>
            {insightData?.fetchUserStreak?.total} {page?.finishingCard?.finishingCardInsight?.insights}
          </strong>
        </>
      ) : (
        dailyInsight?.insight.attributes?.title
      ),
      mindful: mindfulMinuteCompleted ? (
        <>
          {page?.finishingCard?.finishingCardMindfulMinute?.youHaveWatched}{' '}
          <strong>
            {mindfulMinuteData?.fetchUserStreak?.total} {page?.finishingCard?.finishingCardMindfulMinute?.minutes}
          </strong>
        </>
      ) : (
        mindfulMinute?.attributes?.calmBreathing?.title ||
        mindfulMinute?.attributes?.prompt?.title ||
        mindfulMinute?.attributes?.video?.videoTitle
      ),
      emotion: dailyEmotionWheelCompleted ? (
        <>
          {page?.finishingCard?.finishingCardEmotionWheel?.youHaveTracked}{' '}
          <strong>
            {emotionWheelData?.fetchUserStreak?.total} {page?.finishingCard?.finishingCardEmotionWheel?.emotions}
          </strong>
        </>
      ) : (
        page?.dailySection.emotionWheelDescription
      ),
      quote: addQuotes(quote?.attributes?.quote || '“A healthy outside starts from the inside.”'),
    };

    const dailyActivityPrefix = {
      insight: page?.dailySection.prefixInsightCard,
      mindful: page?.dailySection.prefixMindfulMinuteCard,
    } as Record<string, string>;

    const buttonLabel = {
      insight: dailyInsightCompleted
        ? page?.finishingCard?.finishingCardInsight?.readAgain
        : page?.dailySection.insightBtnLabel,
      mindful: mindfulMinuteCompleted
        ? page?.finishingCard?.finishingCardMindfulMinute?.startAgain
        : page?.dailySection.mindfulMinuteBtnLabel,
      emotion: dailyEmotionWheelCompleted
        ? page?.finishingCard?.finishingCardEmotionWheel?.trackAgain
        : page?.dailySection.emotionWheelBtnLabel,
      quote: 'Mark as read',
    };

    const isCompleted = {
      insight: dailyInsightCompleted,
      mindful: mindfulMinuteCompleted,
      emotion: dailyEmotionWheelCompleted,
      quote: dailyQuoteCompleted,
    };

    const link = {
      insight: '/daily-insight',
      mindful: '/mindful-minute',
      emotion: '/emotion-wheel',
      quote: async (quote: QuoteEntity) => {
        await handleCompleteTask({
          entity: quote,
          type: Enum_Completedtask_Type.Quote,
        });
      },
    };

    const renderActivity = () => {
      return (
        <>
          <h3 className="!m-0 font-larken text-sh-18-28">{title[type]}</h3>
          <p className="mt-2 text-sh-12-18">
            {dailyActivityPrefix[type] ? dailyActivityPrefix[type] : ''} <strong>{description[type]}</strong>
          </p>
          {type === 'quote' && (
            <div className="mt-2 flex">
              <div className="flex w-full">
                {/* {parseTextWithLinks(L2?.advice || '', L2?.finalState as ComponentEmotionWheelFinalState)} */}
                <p className="text-sh-12-18 font-bold">{quote?.attributes?.author}</p>
              </div>
            </div>
          )}
          {quote?.attributes?.quoteWithLinks &&
            quote?.attributes?.quote &&
            quote?.attributes?.quote?.length < 121 &&
            type === 'quote' && (
              <div
                key={quote?.attributes?.quoteWithLinks}
                dangerouslySetInnerHTML={{
                  __html: parseTextWithLinks(
                    quote?.attributes?.quoteWithLinks || '',
                    quote?.attributes?.links,
                    '!text-sh-12-18 !text-left mt-[5px]',
                  ),
                }}
              />
            )}

          {type !== 'quote' && <p className="mt-0 text-sh-12-18 font-bold">{buttonLabel[type]}</p>}
        </>
      );
    };

    if (!hasContent[type]) {
      return null;
    }

    return (
      <div
        onClick={() => {
          if (link[type]) {
            if (type === 'insight' || type === 'mindful' || type === 'emotion') {
              navigate(link[type] as string);
            }
          }
        }}
        className={classNames('group h-full w-[310px] overflow-hidden rounded-2xl border-2 border-transparent', {
          'cursor-pointer hover:border-primary-600 ':
            link[type] && (type === 'insight' || type === 'mindful' || type === 'emotion'),
          'cursor-default': !link[type] || type === 'quote' || isCompleted[type],
        })}>
        <div
          className={classNames('flex h-full w-[310px] flex-col justify-start px-sh-30 py-sh-15', {
            [`daily-bubble ${gradients[type]}`]: !isCompleted[type],
            'bg-sh-border-learn': isCompleted[type],
          })}>
          {renderActivity()}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return Object.values(hasContent)
        .filter((content) => !!content)
        .map((_, index) => (
          <div key={index} className="h-[120px] w-[310px] animate-pulse rounded-md bg-gray-300"></div>
        ));
    }
    return [
      renderTaskCard('insight'),
      renderTaskCard('mindful'),
      renderTaskCard('emotion'),
      renderTaskCard('quote'),
    ].filter((content) => !!content);
  };

  if (!isMobile) {
    return renderContent();
  }

  return (
    <>
      {hasContent && (
        <Slider
          disableResponsiveDots={true}
          slideClassName="!max-h-none"
          sliders={renderContent()}
          options={{
            disabled: true,
            breakpoints: {
              '(max-width: 768px)': {
                disabled: false,
                loop: true,
                renderMode: 'performance',
                slides: { perView: 1.2, spacing: 16 },
              },
            },
          }}
        />
      )}
      {!hasContent && renderContent()}
    </>
  );
}
